import React, { useState, useEffect } from 'react';
import TemporaryWhitelist from './TemporaryWhitelist';
import Whitelist from './OnBoard';
import { Typography, Box, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import config from '../config';
import {
  DAPP_ICON,
  DAPP_NAME,
  SYMBOL_NAME,
  TransactionName,
} from '../constants';
import Modal from 'react-bootstrap/Modal';
import ErrorIcon from '@mui/icons-material/Error';
import {
  ExecuteReadTransaction,
  ExecuteSubmitTransaction,
  formattedErrorMessage,
} from '../utils';
import MyVerticallyCenteredModal from './MyVerticallyCenteredModal';

const getErrorMessage = (message) => {
  try {
    // Remove "Error:" prefix if it exists
    if (message.startsWith('Error:')) {
      message = message.replace('Error: ', '');
    }

    // Try to parse as JSON first (for structured errors)
    if (message.startsWith('{')) {
      const parsedError = JSON.parse(message);

      // Check for specific error message and return custom messages
      if (parsedError?.message?.error === 'insufficient balance') {
        return 'Not Authorized!';
      }

      // Return the error message from the parsed object if available
      return parsedError.message?.error || 'Unknown Error';
    }

    // Handle plain text errors
    if (message === 'insufficient balance') {
      return 'Not Authorized!';
    }

    // If no mapping, return the original message
    return message;
  } catch (err) {
    // If parsing fails, return the original message or a fallback
    return message || 'An unexpected error occurred.';
  }
};

const WhitelistManager = ({ address, walletConnected }) => {
  const [activeComponent, setActiveComponent] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [modalShow, setModalShow] = useState(false);

  const handleRenderWhitelist = () => {
    setActiveComponent('whitelist');
  };

  const handleRenderTemporaryWhitelist = () => {
    setActiveComponent('temporaryWhitelist');
  };

  useEffect(() => {
    const fetchInitializationStatus = async () => {
      try {
        if (localStorage.getItem('opensea_initialized') === 'true') {
          setInitialized(true);
          return;
        }
        const dappToken = localStorage.getItem('opensea_dappToken') || '';
        if (!dappToken) return;
        const [res, errMsg] = await ExecuteReadTransaction(
          config.CHANNEL_NAME,
          config.CHAINCODE_NAME,
          'Name',
          []
        );
        console.log('fetchInitializationStatus response: ', res);
        if (res) {
          localStorage.setItem('opensea_initialized', 'true');
          setInitialized(true);
        } else {
          throw new Error(formattedErrorMessage(errMsg));
        }
      } catch (error) {
        console.error('Error fetching enrollmentId:', error);
      }
    };
    fetchInitializationStatus();
  }, [walletConnected]);

  async function handleInitialize() {
    try {
      const transactionParams = [DAPP_NAME, SYMBOL_NAME, config.CHAINCODE_NAME];
      const [res, errMsg] = await ExecuteSubmitTransaction(
        config.CHANNEL_NAME,
        config.CHAINCODE_NAME,
        TransactionName.INITIALIZE,
        transactionParams
      );
      if (res.transactionId) {
        setInitialized(true);
        setResponseMessage(res.transactionId);
      } else {
        setResponseMessage(
          `Error: ${
            formattedErrorMessage(res.error) || formattedErrorMessage(errMsg)
          }`
        );
      }
    } catch (error) {
      console.error('Error initializing the DApp:', error);
      setResponseMessage('An error occurred during the transaction.');
    } finally {
      setModalShow(true);
    }
  }

  return (
    <>
      {!activeComponent && (
        <div
          style={{
            textAlign: 'center',
            width: '600px',
            margin: '0 auto',
            fontFamily: 'Arial, sans-serif',
            minHeight: '35vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f4f5f7',
          }}
        >
          <h2
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '20px',
            }}
          >
            Approve
          </h2>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              maxWidth: '600px',
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '15px',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              marginBottom: '20px',
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  marginBottom: '5px',
                  textAlign: 'left',
                }}
              >
                Your Address:
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 'bold', fontSize: '16px' }}
              >
                {address}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              {initialized ? (
                <>
                  <CheckCircleIcon sx={{ color: '#009688' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#009688',
                      fontWeight: 'bold',
                      fontSize: '14px',
                    }}
                  >
                    Initialization Complete
                  </Typography>
                </>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#0033cc',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '10px',
                    padding: '5px 20px',
                    '&:hover': {
                      backgroundColor: '#0022a6',
                    },
                  }}
                  onClick={handleInitialize}
                >
                  Initialize
                </Button>
              )}
            </Box>
          </Box>

          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '15px 20px',
              marginBottom: '10px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              border: 'none',
              width: '100%',
              textAlign: 'left',
              fontSize: '16px',
              transition: 'transform 0.2s ease',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = 'translateY(-2px)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = 'translateY(0px)')
            }
            onClick={handleRenderWhitelist}
          >
            <b>Whitelist / Blacklist Request</b>
            <span style={{ fontSize: '18px', color: '#888' }}>›</span>
          </button>

          {/*<button style={{
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        backgroundColor: 'white', 
        borderRadius: '8px', 
        padding: '15px 20px', 
        marginBottom: '10px', 
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', 
        cursor: 'pointer', 
        border: 'none', 
        width: '100%', 
        textAlign: 'left', 
        fontSize: '16px', 
        transition: 'transform 0.2s ease'
      }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
         onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0px)'}
         onClick={handleRenderTemporaryWhitelist}>

        <b>Temporary Whitelist Request</b>
        <span style={{ fontSize: '18px', color: '#888' }}>›</span>
      </button>*/}
        </div>
      )}
      {/* Conditionally render components based on the state */}
      {activeComponent === 'whitelist' && (
        <Whitelist goBack={() => setActiveComponent(null)} />
      )}
      {activeComponent === 'temporaryWhitelist' && (
        <TemporaryWhitelist goBack={() => setActiveComponent(null)} />
      )}
      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        message={responseMessage}
        getErrorMessage={getErrorMessage}
      />
    </>
  );
};

export default WhitelistManager;
